import { format, parseISO } from 'date-fns';
import { useContext } from 'react';
import { Button } from 'react-bootstrap';
import {
  BookingRequestRead,
  BookingRequestReadStatusEnum,
  StatusByProviderEnum,
  StatusByRepEnum,
  UserTypeEnum,
} from '../../../api';
import { SessionContext } from '../../contexts/SessionContext';
import { useCurrencyFormatter } from '../../hooks/use-currency-formatter';
import { BookingRequestStatusBadge } from '../BookingRequestStatusBadge';
import { Card } from '../Card';
import { FacilityLogo } from '../FacilityLogo';
import { FacilityRepresentation } from '../FacilityRepresentation/FacilityRepresentation';
import { HealthcareEntityRepRepresentation } from '../HealthcareEntityRepRepresentation';
import { ProviderRepresentation } from '../ProviderRepresentation';

const NumberOfShiftsInfo = ({ shifts }: { shifts: number }) => (
  <div>
    {shifts} {shifts === 1 ? 'shift' : 'shifts'}
  </div>
);

const AcceptedShiftsVsTotal = ({
  acceptedShifts,
  shifts,
}: {
  acceptedShifts: number;
  shifts: number;
}) => {
  if (acceptedShifts === shifts) {
    return (
      <div>
        {shifts > 1 ? 'All ' : ''}
        {shifts} {shifts === 1 ? 'of 1 shift' : 'shifts'} accepted
      </div>
    );
  } else if (acceptedShifts === 0) {
    return (
      <div>
        None of the {shifts} {shifts === 1 ? 'shift' : 'shifts'} accepted
      </div>
    );
  } else {
    return (
      <div>
        {acceptedShifts} of {shifts} {shifts === 1 ? 'shift' : 'shifts'} accepted
      </div>
    );
  }
};

export const BookingRequestCard = ({
  bookingRequest,
  onBookingRequestClick,
}: {
  bookingRequest: BookingRequestRead;
  onBookingRequestClick: (id: string) => void;
}) => {
  const { user } = useContext(SessionContext);
  const { formatCurrency } = useCurrencyFormatter();

  const userIsProvider = user?.user_type === UserTypeEnum.PROVIDER;

  const acceptedShiftsCount = bookingRequest.bookings?.filter((booking) => {
    if (bookingRequest.status === BookingRequestReadStatusEnum.RESPONDED) {
      return booking.status_by_provider === StatusByProviderEnum.ACCEPTED;
    } else if (bookingRequest.status === BookingRequestReadStatusEnum.CONFIRMED) {
      return (
        booking.status_by_provider === StatusByProviderEnum.ACCEPTED &&
        booking.status_by_rep === StatusByRepEnum.ACCEPTED
      );
    } else {
      return false;
    }
  }).length;

  const totalPaymentLabel =
    bookingRequest.status === BookingRequestReadStatusEnum.PENDING
      ? 'Total Proposed Payment'
      : 'Total Payment';

  return (
    <Card className="p-4">
      <div className="d-flex flex-grow-1 align-items-center justify-content-end gap-1">
        <BookingRequestStatusBadge
          status={bookingRequest.status}
          bookings={bookingRequest.bookings}
        />
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-1">
          <i className="far fa-fw fa-lg fa-hospital tw:mb-[5px]" />
          <span className="tw:text-lg">{bookingRequest?.code}</span>
        </div>
        <small className="tw:pr-1">
          {format(parseISO(bookingRequest.updated_at), 'MMM d yyyy')}
        </small>
      </div>
      <FacilityRepresentation facility={bookingRequest.healthcare_facility} className="mt-3" />
      <div className="my-2">
        {userIsProvider ? (
          <>
            <small>Sent by</small>
            <HealthcareEntityRepRepresentation
              healthcareEntityRep={bookingRequest.healthcare_entity_rep}
              showHealthcareOrganization={true}
            />
          </>
        ) : (
          <>
            <small>Provider</small>
            <ProviderRepresentation provider={bookingRequest.provider} />
          </>
        )}
      </div>
      <div className="mb-2">
        <small className="d-block">
          {bookingRequest.status === BookingRequestReadStatusEnum.PENDING
            ? 'Proposed shifts'
            : 'Shifts'}
        </small>
        <div>
          {bookingRequest.status === BookingRequestReadStatusEnum.PENDING ||
          bookingRequest.status === BookingRequestReadStatusEnum.CANCELED ? (
            <NumberOfShiftsInfo shifts={bookingRequest.bookings.length} />
          ) : (
            <AcceptedShiftsVsTotal
              acceptedShifts={acceptedShiftsCount}
              shifts={bookingRequest.bookings.length}
            />
          )}
        </div>
        <div>
          {format(parseISO(bookingRequest.bookings[0].start_on), 'MMM d, yyyy')} -{' '}
          {format(
            parseISO(bookingRequest.bookings[bookingRequest.bookings.length - 1].end_on),
            'MMM d, yyyy'
          )}
        </div>
      </div>
      <Card.Divider space="1.5rem" cardPadding="1.5rem" />
      <div className="d-flex gap-2 justify-content-between align-items-center">
        <Card.Info label={totalPaymentLabel}>
          {formatCurrency(bookingRequest.overall_total)}
        </Card.Info>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onBookingRequestClick(bookingRequest.id)}
        >
          Review Request
        </Button>
      </div>
    </Card>
  );
};
